// The company switcher's rules (FOUNDER-OS; 12, 04 `me/companies?counts=true`), pure so the
// order, the groups, the ⌘-digit keys and the landing are tested on their own:
//   - rows come from `GET /v1/me/companies` (the portfolio first, then "My companies", then
//     "Clients via {agency}"), and from `/v1/me` while that loads or where the API lacks it;
//   - ⌘1…⌘9 (Ctrl+digit) open the nth row from anywhere, never while typing or while a modal owns
//     the keys; plain 1–9 do the same while the menu is open;
//   - switching keeps the current mode when the target company shows it, else lands on the
//     target's default (the company index redirects there).

import type { Band, MeCompanies, MeResponse, SwitcherGroup } from "../api/types";
import { visibleModes } from "../extensions/modes";
import { type CompanyAccess, companiesOf } from "../features/company";
import { isTyping } from "../shortcuts/mode-keys";

export type SwitcherRow = {
  id: string;
  slug: string;
  name: string;
  kind: CompanyAccess["kind"];
  group: SwitcherGroup;
  agencyName: string | null;
  unread: number;
  approvals: number;
  band: Band | null;
  /** The ⌘-number (1–9) of the row, null past the ninth. */
  digit: number | null;
};

export type SwitcherSection = { key: string; label: string; rows: SwitcherRow[] };

const RANK: Record<SwitcherGroup, number> = { portfolio: 0, owned: 1, member: 1, client: 2 };

/** The switcher's rows in order: the server's list when there is one, else `/v1/me`'s. */
export function switcherRows(me: MeResponse, list?: MeCompanies | null): SwitcherRow[] {
  const open = new Map(companiesOf(me).map((c) => [c.id, c]));
  const rows: Omit<SwitcherRow, "digit">[] = list
    ? list.companies
        .filter((c) => open.has(c.company_id))
        .map((c) => ({
          id: c.company_id,
          slug: c.slug,
          name: c.name,
          kind: open.get(c.company_id)?.kind === "portfolio" ? "portfolio" : c.kind,
          group: c.group,
          agencyName: c.agency_name,
          unread: c.unread,
          approvals: c.approvals,
          band: c.band,
        }))
    : [...open.values()]
        .map((c) => ({
          id: c.id,
          slug: c.slug,
          name: c.name,
          kind: c.kind,
          group: (c.kind === "portfolio"
            ? "portfolio"
            : c.roles.includes("owner")
              ? "owned"
              : c.viaAgency
                ? "client"
                : "member") as SwitcherGroup,
          agencyName: null,
          unread: 0,
          approvals: 0,
          band: null,
        }))
        .sort((a, b) => RANK[a.group] - RANK[b.group] || a.name.localeCompare(b.name));
  return rows.map((r, i) => ({ ...r, digit: i < 9 ? i + 1 : null }));
}

/** The menu's sections: "Founder OS", "My companies", then "Clients via {agency}" per agency. */
export function switcherSections(rows: readonly SwitcherRow[]): SwitcherSection[] {
  const out: SwitcherSection[] = [];
  const section = (key: string, label: string) => {
    let s = out.find((x) => x.key === key);
    if (!s) {
      s = { key, label, rows: [] };
      out.push(s);
    }
    return s;
  };
  for (const r of rows) {
    if (r.group === "portfolio") section("portfolio", "Founder OS").rows.push(r);
    else if (r.group === "client")
      section(
        `client:${r.agencyName ?? ""}`,
        r.agencyName ? `Clients via ${r.agencyName}` : "Clients",
      ).rows.push(r);
    else section("mine", "My companies").rows.push(r);
  }
  return out;
}

export type Health = "good" | "warn" | "bad" | "none";

/** The health dot: great/regular green, average amber, low red, none (no band yet) grey. */
export function healthOf(band: Band | null): Health {
  if (band === "great" || band === "regular") return "good";
  if (band === "average") return "warn";
  if (band === "low") return "bad";
  return "none";
}

export const HEALTH_LABEL: Record<Health, string> = {
  good: "on track",
  warn: "below target",
  bad: "off target",
  none: "no KPI yet",
};

type DigitEvent = Pick<
  KeyboardEvent,
  "key" | "code" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey" | "target" | "defaultPrevented"
>;

/** A modal owns the keyboard while it is open (the switcher's own menu does not count). */
function modalOpen(): boolean {
  return (
    typeof document !== "undefined" &&
    document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]') !== null
  );
}

const digitOf = (e: DigitEvent) => {
  const m = /^Digit([1-9])$/.exec(e.code ?? "") ?? /^([1-9])$/.exec(e.key);
  return m ? Number(m[1]) : null;
};

/** ⌘1…⌘9 / Ctrl+1…9: the row to open, or null when the key press is not the switcher's. */
export function switcherDigit(e: DigitEvent): number | null {
  if (e.defaultPrevented || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return null;
  const n = digitOf(e);
  if (n === null || isTyping(e.target) || modalOpen()) return null;
  return n;
}

/** A plain 1–9 while the menu is open. */
export function menuDigit(e: DigitEvent): number | null {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  return digitOf(e);
}

/**
 * Where switching to `target` lands: the current mode when the target shows it (Chart → Chart),
 * else the target's index, which redirects to its default mode.
 */
export function companyLanding(
  me: MeResponse,
  target: CompanyAccess,
  modeKey: string | null,
): { to: string; params: { slug: string } } {
  const keep = modeKey ? visibleModes(me, target).find((m) => m.key === modeKey) : undefined;
  return { to: keep ? keep.path : "/c/$slug", params: { slug: target.slug } };
}
